/** Товары БЕЗ движения ни на одной площадке — на экране распределения.
 *
 *  Товары экрана собираются из строк отчётов спроса, а отчёты строятся по движению (заказы /
 *  остаток / в пути). Новинка, у которой уже есть спека с `mp_list` и карточка МП, но ещё не было
 *  ни одного движения, в отчёты не попадает вовсе — и вписать под неё «Решение», пока она в
 *  производстве, было некуда (NGG-142, 12.10.2026). Такие товары сеются из карточек 1С
 *  (`GET /supply/channel-skus`): дальше обычная достройка нулевых строк заводит им каналы с sku и
 *  ставит бейдж «нет артикула» там, где sku не заполнен.
 *
 *  Чистые функции — чтобы правило было проверяемо без рендера страницы.
 */

import type { ChannelCard } from "@/api/supply";

/** Ключ «канал + номенклатура» — тот же, что у мостов sku на странице распределения. */
export const cardKey = (channel: string, nom: string): string =>
  `${channel}\u0000${nom}`;

/** Товар, засеянный из карточек: имя и артикул — свойства номенклатуры, у всех её карточек одни. */
export interface ProductSeed {
  nom_key: string;
  name: string;
  article: string;
}

/** Номенклатуры из карточек, которых НЕТ среди товаров, собранных из отчётов (`known`). Одна на
 *  номенклатуру, в порядке первой карточки. Имя/артикул пустые → пустая строка: выдумывать их
 *  нечем, а поиск по артикулу честно не найдёт товар без артикула. */
export function newProductSeeds(
  cards: readonly ChannelCard[],
  known: (nom: string) => boolean,
): ProductSeed[] {
  const out = new Map<string, ProductSeed>();
  for (const c of cards) {
    if (known(c.nom_key)) continue;
    const prev = out.get(c.nom_key);
    if (!prev) {
      out.set(c.nom_key, {
        nom_key: c.nom_key,
        name: c.name ?? "",
        article: c.article ?? "",
      });
      continue;
    }
    if (!prev.name && c.name) prev.name = c.name;
    if (!prev.article && c.article) prev.article = c.article;
  }
  return [...out.values()];
}

/** Квант (штук в коробе) по карточке канала: только заданные (> 0). Ключ — `cardKey`. */
export function cardInBoxMap(
  cards: readonly ChannelCard[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of cards)
    if (c.in_box != null && c.in_box > 0)
      m.set(cardKey(c.channel, c.nom_key), c.in_box);
  return m;
}

/** Квант достроенной строки канала. Сначала — у соседней строки ЭТОГО канала (квант — свойство
 *  упаковки, а не региона; у соседнего МП упаковка своя, её не берём). Соседей с квантом нет
 *  (новинка, канал без движения) — со спеки канала в 1С. Нет и там — 0: ячейка честно покажет
 *  прочерк. */
export function fillInBox(
  neighbours: readonly { in_box: number }[],
  fromCard: number | undefined,
): number {
  return neighbours.find((l) => l.in_box > 0)?.in_box ?? fromCard ?? 0;
}

/** Что делать с засеянным товаром ПОСЛЕ достройки каналов:
 *  - `show` — есть хоть один канал со строками: планировать есть куда;
 *  - `warn` — каналов нет, но есть «нет артикула»: таблица строится из строк, и такой товар в неё
 *    не попадёт (бейджу негде стоять) — поэтому о нём говорим в предупреждениях данных, а не молчим;
 *  - `drop` — каналов нет и sku не пропущен: у его каналов нет ни одной включённой цели (выключены
 *    в «Куда планируем», а без отчёта канала целей нет ни у кого) — планировать нечего, и это не
 *    дефект данных. */
export function seededVerdict(p: {
  channels: readonly unknown[];
  noSku: readonly string[];
}): "show" | "warn" | "drop" {
  if (p.channels.length > 0) return "show";
  return p.noSku.length > 0 ? "warn" : "drop";
}

/** Каналы, чей отчёт спроса не загрузился (нет данных или ошибка). Пока список не пуст, товары без
 *  движения НЕ сеем: товар, движущийся только на упавшем канале, иначе выглядел бы «без движения»
 *  на остальных — молчаливая маскировка ошибки. */
export function failedDemandChannels(
  reports: readonly { label: string; loaded: boolean; failed: boolean }[],
): string[] {
  return reports.filter((r) => !r.loaded || r.failed).map((r) => r.label);
}
